import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useQueryStates, parseAsString } from 'nuqs';
import { Card } from './components/ui/Card';
import { Select } from './components/ui/Select';
import { Button } from './components/ui/Button';
import { useApi } from './lib/api';
import { fetchJson } from './lib/apiFetchJson';
import {
  UsageArtifactTypesResponseSchema,
  UsageByRepResponseSchema,
  type UsageByTypeEntry,
  UsageByTypeResponseSchema,
  UsageRepDetailResponseSchema,
  UsageStatisticsResponseSchema,
  UsageTypeActionsResponseSchema,
} from '@shared/schemas/admin-usage';
import { typography } from './theme';

// ── URL param definitions ──────────────────────────────────────────

const usageDashParams = {
  startDate: parseAsString,
  endDate: parseAsString,
  artifactType: parseAsString,
};

// ── Helpers ────────────────────────────────────────────────────────

function buildQs(params: Record<string, string | null | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) qs.set(k, v);
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

function fmtPct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function fmtDate(iso: string | Date | null | undefined): string {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ── Sortable Table ─────────────────────────────────────────────────

type SortKey = 'ownerEmail' | 'totalArtifacts' | 'readCount' | 'readRate' | 'lastReadAt';

type TypeSortKey = 'artifactType' | 'count' | 'pctOfTotal' | 'readCount' | 'unread';

function SortIcon({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return <span className="text-slate-300 ml-1">&#8597;</span>;
  return <span className="text-indigo-500 ml-1">{dir === 'asc' ? '\u25B2' : '\u25BC'}</span>;
}

// ── Main Component ─────────────────────────────────────────────────

export default function AdminUsageDashboard() {
  const { apiFetch } = useApi();
  const [params, setParams] = useQueryStates(usageDashParams, { history: 'replace' });
  const { startDate, endDate, artifactType } = params;

  // ── Data queries ──

  const repsQ = useQuery({
    queryKey: ['admin', 'usage', 'reps', startDate, endDate, artifactType],
    queryFn: async () => {
      const qs = buildQs({ startDate, endDate, artifactType });
      return fetchJson(apiFetch, UsageByRepResponseSchema, `/api/admin/usage/by-rep${qs}`);
    },
    staleTime: 0,
  });

  const typesQ = useQuery({
    queryKey: ['admin', 'usage', 'types'],
    queryFn: () => fetchJson(apiFetch, UsageArtifactTypesResponseSchema, '/api/admin/usage/artifact-types'),
    staleTime: 0,
  });

  const statsQ = useQuery({
    queryKey: ['admin', 'usage', 'stats', startDate, endDate, artifactType],
    queryFn: async () => {
      const qs = buildQs({ startDate, endDate, artifactType });
      return fetchJson(apiFetch, UsageStatisticsResponseSchema, `/api/admin/usage/statistics${qs}`);
    },
    staleTime: 0,
  });

  const byTypeQ = useQuery({
    queryKey: ['admin', 'usage', 'by-type', startDate, endDate, artifactType],
    queryFn: async () => {
      const qs = buildQs({ startDate, endDate, artifactType });
      return fetchJson(apiFetch, UsageByTypeResponseSchema, `/api/admin/usage/by-type${qs}`);
    },
    staleTime: 0,
  });

  // ── Expanded artifact-type action breakdown ──

  const [expandedType, setExpandedType] = useState<string | null>(null);

  const typeActionsQ = useQuery({
    queryKey: ['admin', 'usage', 'type-actions', expandedType, startDate, endDate],
    queryFn: async () => {
      // The expanded type IS the artifactType filter for this drill-down.
      const qs = buildQs({ artifactType: expandedType, startDate, endDate });
      return fetchJson(apiFetch, UsageTypeActionsResponseSchema, `/api/admin/usage/type-actions${qs}`);
    },
    enabled: !!expandedType,
    staleTime: 0,
  });

  // ── Expanded rep detail ──

  const [expandedEmail, setExpandedEmail] = useState<string | null>(null);

  const detailQ = useQuery({
    queryKey: ['admin', 'usage', 'rep-detail', expandedEmail, startDate, endDate, artifactType],
    queryFn: async () => {
      const qs = buildQs({ email: expandedEmail, startDate, endDate, artifactType });
      return fetchJson(apiFetch, UsageRepDetailResponseSchema, `/api/admin/usage/rep-detail${qs}`);
    },
    enabled: !!expandedEmail,
    staleTime: 0,
  });

  // ── Sorting ──

  const [sortKey, setSortKey] = useState<SortKey>('totalArtifacts');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return key;
      }
      setSortDir('desc');
      return key;
    });
  }, []);

  const sortedReps = useMemo(() => {
    const reps = [...(repsQ.data?.reps || [])];
    reps.sort((a, b) => {
      let cmp = 0;
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null && bVal == null) cmp = 0;
      else if (aVal == null) cmp = -1;
      else if (bVal == null) cmp = 1;
      else if (typeof aVal === 'string' && typeof bVal === 'string') cmp = aVal.localeCompare(bVal);
      else cmp = (aVal as number) - (bVal as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return reps;
  }, [repsQ.data?.reps, sortKey, sortDir]);

  // ── By-type sorting ──

  const [typeSortKey, setTypeSortKey] = useState<TypeSortKey>('count');
  const [typeSortDir, setTypeSortDir] = useState<'asc' | 'desc'>('desc');

  const handleTypeSort = useCallback((key: TypeSortKey) => {
    setTypeSortKey((prev) => {
      if (prev === key) {
        setTypeSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return key;
      }
      setTypeSortDir('desc');
      return key;
    });
  }, []);

  const sortedByTypeRows = useMemo(() => {
    const typeVal = (row: UsageByTypeEntry): number | string => {
      switch (typeSortKey) {
        case 'artifactType': return row.artifactType;
        case 'count': return row.count;
        case 'pctOfTotal': return row.count; // monotonic with count (shared denominator)
        case 'readCount': return row.readCount;
        case 'unread': return row.count - row.readCount;
      }
    };
    const rows = [...(byTypeQ.data?.types || [])];
    rows.sort((a, b) => {
      const aVal = typeVal(a);
      const bVal = typeVal(b);
      let cmp = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') cmp = aVal.localeCompare(bVal);
      else cmp = (aVal as number) - (bVal as number);
      return typeSortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [byTypeQ.data?.types, typeSortKey, typeSortDir]);

  // ── Filter handlers ──

  const handleReset = () => {
    void setParams({ startDate: null, endDate: null, artifactType: null });
  };

  // ── Loading / error states ──

  const types = typesQ.data?.types || [];

  if (repsQ.error) {
    return (
      <div className="max-w-5xl mx-auto p-4">
        <div className="text-red-600">Error: {(repsQ.error).message}</div>
      </div>
    );
  }

  const earliestDate = statsQ.data?.earliestDate ?? null;
  const byTypeRows = sortedByTypeRows;
  const byTypeTotal = byTypeQ.data?.total || 0;
  const byTypeReadTotal = byTypeQ.data?.readTotal || 0;

  return (
    <div className="max-w-5xl mx-auto p-4 font-sans space-y-6">
      <h2 className={`${typography.headingMd}`}>Usage Dashboard</h2>
      <p className="text-sm text-slate-500">
        Artifact activity by GTME, sourced from v4 artifacts.
      </p>

      {/* ── Data-availability warning ── */}
      {earliestDate && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Usage data is only available from <span className="font-medium">{fmtDate(earliestDate)}</span> onward.
          Activity before this date was tracked in the legacy system and is not shown here.
        </div>
      )}

      {/* ── Filter bar ── */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate || ''}
              onChange={(e) => { void setParams({ startDate: e.target.value || null }); }}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">End Date</label>
            <input
              type="date"
              value={endDate || ''}
              onChange={(e) => { void setParams({ endDate: e.target.value || null }); }}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <div className="w-48">
            <label className="block text-xs text-slate-500 mb-1">Artifact Type</label>
            <Select
              value={artifactType || ''}
              onChange={(e) => {
                const val = (e.target as HTMLSelectElement).value;
                void setParams({ artifactType: val || null });
              }}
            >
              <option value="">All types</option>
              {types.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </div>
          <Button variant="ghost" size="sm" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </Card>

      {/* ── By artifact type ── */}
      <Card>
        <h3 className={`${typography.headingSm} mb-3`}>By Artifact Type</h3>
        {byTypeQ.isLoading ? (
          <div className="animate-pulse space-y-2">
            {[...Array(5)].map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key -- skeleton placeholder, fixed length, never reorders
              <div key={i} className="h-8 rounded bg-slate-100" />
            ))}
          </div>
        ) : byTypeQ.error ? (
          <div className="text-sm text-red-600 py-4 text-center">Error: {byTypeQ.error.message}</div>
        ) : byTypeRows.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No artifact data</div>
        ) : (
          <div className="overflow-x-auto">
            <p className="text-xs text-slate-400 mb-2">Click a row to see its action breakdown.</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  {([
                    ['artifactType', 'Artifact Type'],
                    ['count', 'Count'],
                    ['pctOfTotal', '% of Total'],
                    ['readCount', 'Read'],
                    ['unread', 'Unread'],
                  ] as [TypeSortKey, string][]).map(([key, label]) => (
                    <th
                      key={key}
                      className="py-2 px-2 font-medium text-slate-600 cursor-pointer select-none hover:text-slate-900"
                      onClick={() => handleTypeSort(key)}
                    >
                      {label}
                      <SortIcon active={typeSortKey === key} dir={typeSortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byTypeRows.map((row) => {
                  const unread = row.count - row.readCount;
                  const pct = (n: number) => (row.count > 0 ? `${((n / row.count) * 100).toFixed(0)}%` : '--');
                  const isExpanded = expandedType === row.artifactType;
                  return (
                    <React.Fragment key={row.artifactType}>
                      <tr
                        className={`border-b border-slate-100 cursor-pointer transition-colors ${
                          isExpanded ? 'bg-indigo-50' : 'hover:bg-slate-50'
                        }`}
                        onClick={() => setExpandedType(isExpanded ? null : row.artifactType)}
                      >
                        <td className="py-2 px-2 font-medium">{row.artifactType}</td>
                        <td className="py-2 px-2">{row.count.toLocaleString()}</td>
                        <td className="py-2 px-2 text-slate-500">
                          {byTypeTotal > 0 ? `${((row.count / byTypeTotal) * 100).toFixed(1)}%` : '--'}
                        </td>
                        <td className="py-2 px-2">
                          {row.readCount.toLocaleString()}{' '}
                          <span className="text-slate-400">({pct(row.readCount)})</span>
                        </td>
                        <td className="py-2 px-2">
                          {unread.toLocaleString()} <span className="text-slate-400">({pct(unread)})</span>
                        </td>
                      </tr>

                      {/* ── Expanded action breakdown ── */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={5} className="p-0">
                            <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
                              {typeActionsQ.isLoading ? (
                                <div className="animate-pulse space-y-2">
                                  <div className="h-5 w-48 rounded bg-slate-200" />
                                  <div className="h-24 rounded bg-slate-100" />
                                </div>
                              ) : typeActionsQ.error ? (
                                <div className="text-xs text-red-600 py-2">Error: {typeActionsQ.error.message}</div>
                              ) : (
                                <>
                                  <div className="text-xs font-medium text-slate-600 mb-2">
                                    Action breakdown — {row.artifactType}
                                  </div>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-slate-200 text-left text-slate-500">
                                        <th className="py-1 px-2">Action</th>
                                        <th className="py-1 px-2">Count</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(typeActionsQ.data?.actions || []).map((a) => (
                                        <tr key={a.actionId} className="border-b border-slate-100">
                                          <td className="py-1 px-2">
                                            <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                                              {a.action}
                                            </span>
                                          </td>
                                          <td className="py-1 px-2">{a.count.toLocaleString()}</td>
                                        </tr>
                                      ))}
                                      <tr className="border-b border-slate-100 text-slate-400">
                                        <td className="py-1 px-2 italic">No action taken</td>
                                        <td className="py-1 px-2">
                                          {(typeActionsQ.data?.noActionCount ?? 0).toLocaleString()}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                  {!typeActionsQ.data?.actions?.length && (
                                    <div className="text-xs text-slate-400 mt-2">
                                      No actions taken on {row.artifactType} artifacts in this range.
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr className="border-t-2 border-slate-300 font-medium">
                  <td className="py-2 px-2">Total</td>
                  <td className="py-2 px-2">{byTypeTotal.toLocaleString()}</td>
                  <td className="py-2 px-2 text-slate-500">100%</td>
                  <td className="py-2 px-2">{byTypeReadTotal.toLocaleString()}</td>
                  <td className="py-2 px-2">{(byTypeTotal - byTypeReadTotal).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Rep table ── */}
      <Card>
        <h3 className={`${typography.headingSm} mb-3`}>By GTME</h3>
        {repsQ.isLoading ? (
          <div className="animate-pulse space-y-2">
            {[...Array(5)].map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key -- skeleton placeholder, fixed length, never reorders
              <div key={i} className="h-8 rounded bg-slate-100" />
            ))}
          </div>
        ) : sortedReps.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No rep data</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  {([
                    ['ownerEmail', 'Email'],
                    ['totalArtifacts', 'Total'],
                    ['readCount', 'Read'],
                    ['readRate', 'Read Rate'],
                    ['lastReadAt', 'Last Read'],
                  ] as [SortKey, string][]).map(([key, label]) => (
                    <th
                      key={key}
                      className="py-2 px-2 font-medium text-slate-600 cursor-pointer select-none hover:text-slate-900"
                      onClick={() => handleSort(key)}
                    >
                      {label}
                      <SortIcon active={sortKey === key} dir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedReps.map((rep) => {
                  const email = rep.ownerEmail || '(unknown)';
                  const isExpanded = expandedEmail === rep.ownerEmail;
                  return (
                    <React.Fragment key={email}>
                      <tr
                        className={`border-b border-slate-100 cursor-pointer transition-colors ${
                          isExpanded ? 'bg-indigo-50' : 'hover:bg-slate-50'
                        }`}
                        onClick={() => setExpandedEmail(isExpanded ? null : rep.ownerEmail)}
                      >
                        <td className="py-2 px-2 font-medium">{email}</td>
                        <td className="py-2 px-2">{rep.totalArtifacts.toLocaleString()}</td>
                        <td className="py-2 px-2">{rep.readCount.toLocaleString()}</td>
                        <td className="py-2 px-2">{fmtPct(rep.readRate)}</td>
                        <td className="py-2 px-2 text-slate-500">{fmtDate(rep.lastReadAt)}</td>
                      </tr>

                      {/* ── Expanded rep detail ── */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={5} className="p-0">
                            <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
                              {detailQ.isLoading ? (
                                <div className="animate-pulse space-y-2">
                                  <div className="h-6 w-60 rounded bg-slate-200" />
                                  <div className="h-40 rounded bg-slate-100" />
                                </div>
                              ) : detailQ.error ? (
                                <div className="text-sm text-red-600 py-2">Error: {detailQ.error.message}</div>
                              ) : !detailQ.data?.artifacts?.length ? (
                                <div className="text-sm text-slate-400 py-2">No artifacts found</div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-slate-200 text-left text-slate-500">
                                        <th className="py-1 px-2">Name</th>
                                        <th className="py-1 px-2">Type</th>
                                        <th className="py-1 px-2">Timestamp</th>
                                        <th className="py-1 px-2">Read</th>
                                        <th className="py-1 px-2">Read At</th>
                                        <th className="py-1 px-2">Artifact</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detailQ.data.artifacts.map((a) => (
                                        <tr key={a.id} className="border-b border-slate-100">
                                          <td className="py-1 px-2 max-w-[200px] truncate">{a.artifactName || '--'}</td>
                                          <td className="py-1 px-2">
                                            <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                                              {a.artifactType || '--'}
                                            </span>
                                          </td>
                                          <td className="py-1 px-2 text-slate-500">{fmtDateTime(a.timestamp)}</td>
                                          <td className="py-1 px-2">
                                            {a.read ? (
                                              <span className="text-emerald-600 font-medium">Yes</span>
                                            ) : (
                                              <span className="text-slate-400">No</span>
                                            )}
                                          </td>
                                          <td className="py-1 px-2 text-slate-500">{fmtDateTime(a.readAt)}</td>
                                          <td className="py-1 px-2">
                                            {a.artifactUuid ? (
                                              <a
                                                href={`/artifact/${a.artifactUuid}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-indigo-600 hover:underline"
                                              >
                                                Open
                                              </a>
                                            ) : (
                                              <span className="text-slate-400">--</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {detailQ.data.artifacts.length >= 500 && (
                                    <div className="text-xs text-slate-400 mt-2">Showing first 500 artifacts</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
